import type { ComponentProps, ReactNode } from 'react'
import { useMemo } from 'react'

import { classNames } from '@/utils/helpers'

const allBreakpoints = [
  'xs',
  '!xs',
  'sm',
  '!sm',
  'md',
  '!md',
  'lg',
  '!lg',
  'xl',
  '!xl',
  '2xl',
  '!2xl',
] as const

export type Breakpoint = (typeof allBreakpoints)[number]

export const allParts = ['title', 'details', 'footer'] as const
export type Part = (typeof allParts)[number]
type PartStyle<T> = Partial<Record<Part, T>>
type Positions = PartStyle<Breakpoint[]>
type Ordering = PartStyle<number>
type ClassNames = PartStyle<string>

export interface Component<T> {
  name: string
  render: (value: T, part: Part) => ReactNode
  order?: Ordering
  positions: Positions
  classNames?: ClassNames
}

export type DetailsCardProps<T> = ComponentProps<'article'> & {
  components: Component<T>[]
  value: T
}

function pickDisplayClass(base: string, fallback = 'flex') {
  const parts = base.split(' ')

  for (const option of ['flex', 'inline-flex', 'block', 'inline-block']) {
    if (parts.includes(option)) {
      return option
    }
  }

  return fallback
}

function buildClassNames(
  base: string,
  breakpoints: Breakpoint[] | undefined = undefined,
  fallbackDisplay = 'flex',
) {
  const display = pickDisplayClass(base, fallbackDisplay)

  if (!breakpoints) {
    return classNames(base, display)
  }
  const parts: string[] = []

  for (const breakpoint of allBreakpoints) {
    const withoutNegation = breakpoint.replace('!', '')
    const prefix = withoutNegation === 'xs' ? '' : `${withoutNegation}:`

    if (breakpoints.includes(breakpoint)) {
      if (breakpoint.startsWith('!')) {
        parts.push(`${prefix}hidden`)
      } else {
        parts.push(prefix + display)
      }
    } else if (breakpoint === 'xs') {
      // If xs is not present then hide it by default
      parts.push('hidden')
    }
  }

  return classNames(base, ...parts)
}

const sortByOrder = (left: { order: number }, right: { order: number }) =>
  left.order - right.order

function hasPositions<T>(component: Component<T>, part: keyof Positions) {
  const positions = component.positions?.[part]

  return positions && positions.length > 0
}

interface ComputedPart {
  node: ReactNode
  key: string
  name: string
  className: string
  order: number
}

const defaultDisplay = {
  title: 'flex',
  details: 'block',
  footer: 'flex',
} as const

function useComputedComponents<T>(components: Component<T>[], value: T) {
  return useMemo(() => {
    const computed: Record<Part, ComputedPart[]> = {
      footer: [],
      details: [],
      title: [],
    }

    for (const component of components) {
      for (const part of allParts) {
        if (!hasPositions(component, part)) {
          continue
        }
        const node = component.render(value, part)
        const className = component.classNames?.[part] ?? ''
        const positions = component.positions?.[part] ?? []
        const order = component.order?.[part] ?? 0

        computed[part].push({
          node,
          key: component.name,
          name: component.name,
          className: buildClassNames(
            className,
            positions,
            defaultDisplay[part],
          ),
          order,
        })
      }
    }

    return {
      title: computed.title.sort(sortByOrder),
      details: computed.details.sort(sortByOrder),
      footer: computed.footer.sort(sortByOrder),
    }
  }, [components, value])
}

const Title = ({ className, children, ...props }: ComponentProps<'header'>) => (
  <header
    {...props}
    className={classNames('flex items-center gap-2', className)}
  >
    {children}
  </header>
)

const Footer = ({
  className,
  children,
  ...props
}: ComponentProps<'footer'>) => (
  <footer
    {...props}
    className={classNames(
      'mt-4 flex justify-end items-center gap-4',
      className,
    )}
  >
    {children}
  </footer>
)

const FieldList = ({
  className,
  children,
  ...props
}: React.ComponentProps<'dl'>) => (
  <dl
    {...props}
    className={classNames('grid gap-4 grid-cols-12', className)}
  >
    {children}
  </dl>
)

const Field = ({
  name,
  children,
  ...props
}: { name: string } & React.ComponentProps<'div'>) => (
  <div {...props}>
    <dt className="text-text-sub text-xs">{name}</dt>
    <dd className="mt-1 text-text-default font-semibold">{children}</dd>
  </div>
)

export const Details = <T,>(props: DetailsCardProps<T>) => {
  const { components, value, className, ...rest } = props
  const { title, details, footer } = useComputedComponents(components, value)

  return (
    <article
      {...rest}
      className={classNames('bg-card rounded-lg p-4', className)}
    >
      {title.length !== 0 && (
        <Title>
          {title.map(({ key, className, node }) => (
            <div
              key={key}
              className={className}
            >
              {node}
            </div>
          ))}
        </Title>
      )}
      {details.length !== 0 && (
        <FieldList className={title.length !== 0 ? 'mt-4' : ''}>
          {details.map(({ key, name, className, node }) => (
            <Field
              key={key}
              name={name}
              className={className}
            >
              {node}
            </Field>
          ))}
        </FieldList>
      )}
      {footer.length !== 0 && (
        <Footer>
          {footer.map(({ key, node, className }) => (
            <div
              key={key}
              className={className}
            >
              {node}
            </div>
          ))}
        </Footer>
      )}
    </article>
  )
}

Details.Title = Title
Details.FieldList = FieldList
Details.Field = Field
Details.Footer = Footer
